import React from 'react';
import { Outlet } from 'react-router-dom';

import PublicHeader from '../public/PublicHeader';
import PublicFooter from '../public/PublicFooter';
import { CollectionScope } from '../../contexts/CollectionScopeContext';

/**
 * The frame for every page a visitor with NO account can read: the shared
 * public header, the page, and the shared public footer (KPE-008).
 *
 * Two kinds of page sit in it:
 *
 *   - a page someone handed the visitor — `/s/:code` (a shared view, item or
 *     card, an intake form) and `/f/:collectionSlug/:formSlug` (a public
 *     form). The header is the bare wordmark: no account chrome around
 *     somebody's records (platform-standards §13.6).
 *   - the public SITE (`site`): the landing page at `/` signed out
 *     (pages/RootFrame.js), the legal pages, the email preferences page, and
 *     later Contact and Status. Its header carries Docs, Sign in and Sign up.
 *
 * Every page sits in the same centred column with the same gutters, so a
 * page sets only its own reading width inside it.
 *
 * It exists so those pages sit OUTSIDE the app shell (`Layout`), and the
 * shell's own plumbing never mounts around them: the rail's pinned
 * collections, the command palette warming the card cache, the inbox. Each of
 * those is an authenticated read, and on a public page each one is a 401 —
 * wasted at best, and on a path the api client does not treat as public, a
 * hard redirect to the login page.
 *
 * Nothing beneath it is ever a QUALIFIED badge (contexts/CollectionScopeContext):
 * a share link discloses the records it names and the titles its lookups
 * resolve to (keepr-api `buildLookupTitles`), never which collection anything
 * lives in — and the resolver behind the qualifier is an authenticated read.
 *
 * As a layout route it renders the matched child (<Outlet />); as a wrapper
 * (RootFrame, and the build-time prerender of `/`) it renders its children.
 */
export default function PublicShell({ site = false, children }) {
	return (
		<div className="public-shell">
			<a className="public-shell__skip" href="#public-main">Skip to content</a>
			<PublicHeader site={site} />
			<main id="public-main" className="public-shell__work" tabIndex={-1}>
				<CollectionScope qualify={false}>
					{children === undefined ? <Outlet /> : children}
				</CollectionScope>
			</main>
			<PublicFooter />
		</div>
	);
}
